import React, { useEffect, useState } from "react";
import {
  X,
  Check,
  User,
  Activity,
  Pill,
  Book,
  Briefcase,
  FileSignature,
  Share2,
  FileText,
  Loader2,
} from "lucide-react";
import IpdService from "../../../../Services/IpdService";
import moment from "moment";

export default function NurseDischargeModal({
  isOpen,
  onClose,
  admissionNo,
  patientId,
}) {
  const [loading, setLoading] = useState(false);
  const [dischargeData, setDischargeData] = useState(null);

  useEffect(() => {
    if (isOpen && admissionNo && patientId) {
      fetchDischargeDetails();
    }
  }, [isOpen, admissionNo, patientId]);

  const fetchDischargeDetails = async () => {
    setLoading(true);
    try {
      const response = await IpdService.getPatientNurseDischargeList({
        admissionNo: admissionNo || "",
        patientId: patientId || "",
      });
      if (response.data && response.data.length > 0) {
        setDischargeData(response.data[0]);
      } else {
        setDischargeData(null);
      }
    } catch (error) {
      console.error("Error fetching nurse discharge details", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getChecklistBySection = (sectionCode) => {
    if (!dischargeData || !dischargeData.patientNurseDischargeChecklist)
      return [];
    return dischargeData.patientNurseDischargeChecklist.filter(
      (item) => item.checklistSectionCode === sectionCode,
    );
  };

  const patientReadiness = getChecklistBySection("PATIENT_READINESS");
  const medications = getChecklistBySection("MEDICATIONS");
  const patientEducation = getChecklistBySection("PATIENT_EDUCATION");
  const documents = getChecklistBySection("DOCUMENTS");
  const patientBelongings = getChecklistBySection("PATIENT_BELONGINGS");

  const isCompleted = (item) => item.completedYn === "Y";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh] animate-in fade-in zoom-in duration-200">
        <div className="bg-white px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <div className="flex items-center gap-3 text-teal-800">
            <div className="bg-teal-50 p-2 rounded-full">
              <User className="w-5 h-5 text-teal-600" />
            </div>
            <h3 className="font-semibold text-lg text-gray-800">
              10. Nurse Discharge Checklist{" "}
              <span className="text-sm font-normal text-gray-500">
                (Read Only)
              </span>
            </h3>
            <span className="text-xs text-gray-500 mt-1 block font-normal">
              Checklist completed by nursing staff during discharge
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100">
              <div className="bg-emerald-500 rounded-full w-4 h-4 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
              Completed
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-black/5"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 bg-white relative">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
          ) : !dischargeData ? (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No discharge details found.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* 1. Patient Readiness */}
              {patientReadiness.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <h4 className="font-semibold text-pink-600 mb-4 text-sm flex items-center gap-2">
                    <Activity className="w-4 h-4" /> Patient Readiness
                  </h4>
                  <div className="space-y-3">
                    {patientReadiness.map((item, idx) => (
                      <label
                        key={idx}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <div
                          className={`mt-0.5 min-w-[16px] w-4 h-4 rounded flex items-center justify-center shadow-sm ${
                            isCompleted(item) ? "bg-teal-500" : "bg-gray-200"
                          }`}
                        >
                          {isCompleted(item) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-gray-700 text-sm font-medium leading-tight">
                          {item.checklistName}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 2. Medications */}
              {medications.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <h4 className="font-semibold text-orange-500 mb-4 text-sm flex items-center gap-2">
                    <Pill className="w-4 h-4" /> Medications
                  </h4>
                  <div className="space-y-3">
                    {medications.map((item, idx) => (
                      <label
                        key={idx}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <div
                          className={`mt-0.5 min-w-[16px] w-4 h-4 rounded flex items-center justify-center shadow-sm ${
                            isCompleted(item) ? "bg-teal-500" : "bg-gray-200"
                          }`}
                        >
                          {isCompleted(item) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-gray-700 text-sm font-medium leading-tight">
                          {item.checklistName}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 3. Patient Education */}
              {patientEducation.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <h4 className="font-semibold text-blue-600 mb-4 text-sm flex items-center gap-2">
                    <Book className="w-4 h-4" /> Patient Education
                  </h4>
                  <div className="space-y-3">
                    {patientEducation.map((item, idx) => (
                      <label
                        key={idx}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <div
                          className={`mt-0.5 min-w-[16px] w-4 h-4 rounded flex items-center justify-center shadow-sm ${
                            isCompleted(item) ? "bg-teal-500" : "bg-gray-200"
                          }`}
                        >
                          {isCompleted(item) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-gray-700 text-sm font-medium leading-tight">
                          {item.checklistName}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 4. Documents */}
              {documents.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <h4 className="font-semibold text-indigo-500 mb-4 text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Documents
                  </h4>
                  <div className="space-y-3">
                    {documents.map((item, idx) => (
                      <label
                        key={idx}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <div
                          className={`mt-0.5 min-w-[16px] w-4 h-4 rounded flex items-center justify-center shadow-sm ${
                            isCompleted(item) ? "bg-teal-500" : "bg-gray-200"
                          }`}
                        >
                          {isCompleted(item) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-gray-700 text-sm font-medium leading-tight">
                          {item.checklistName}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 5. Patient Belongings */}
              {patientBelongings.length > 0 && (
                <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                  <h4 className="font-semibold text-emerald-500 mb-4 text-sm flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Patient Belongings
                  </h4>
                  <div className="space-y-3">
                    {patientBelongings.map((item, idx) => (
                      <label
                        key={idx}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <div
                          className={`mt-0.5 min-w-[16px] w-4 h-4 rounded flex items-center justify-center shadow-sm ${
                            isCompleted(item) ? "bg-teal-500" : "bg-gray-200"
                          }`}
                        >
                          {isCompleted(item) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-gray-700 text-sm font-medium leading-tight">
                          {item.checklistName}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 6. Nurse Details */}
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                <h4 className="font-semibold text-purple-600 mb-4 text-sm flex items-center gap-2">
                  <FileSignature className="w-4 h-4" /> Nurse Details
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Discharged By (Nurse)
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={dischargeData.nurseName || ""}
                      className="w-full bg-gray-50/80 border border-gray-100 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none pointer-events-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Staff ID
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={dischargeData.nurseId || ""}
                      className="w-full bg-gray-50/80 border border-gray-100 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none pointer-events-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Discharge Time
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={
                        dischargeData.dischargeTime
                          ? moment(dischargeData.dischargeTime).format(
                              "DD MMM YYYY, hh:mm A",
                            )
                          : ""
                      }
                      className="w-full bg-gray-50/80 border border-gray-100 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none pointer-events-none"
                    />
                  </div>
                </div>
              </div>

              {/* 7. Final Handover */}
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                <h4 className="font-semibold text-teal-600 mb-4 text-sm flex items-center gap-2">
                  <Share2 className="w-4 h-4" /> Final Handover
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Handover To (Patient / Relative)
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={dischargeData.handoverTo || ""}
                      className="w-full bg-gray-50/80 border border-gray-100 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none pointer-events-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Relationship
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={dischargeData.handoverRelationship || ""}
                      className="w-full bg-gray-50/80 border border-gray-100 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none pointer-events-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Contact Number
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={dischargeData.handoverContactNumber || ""}
                      className="w-full bg-gray-50/80 border border-gray-100 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none pointer-events-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors font-medium text-sm shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
